import { mergeLeft } from 'ramda';
import { useCallback, useState, useEffect, useRef } from 'react';
import {
  Status,
  raiseOnServerError,
  isNetworkError,
  isAborted,
  NetworkError,
  AbortError,
} from './requests/fetchUtils';

/**
 * Builder
 */
const getOptions = (
  options: RequestInit | undefined,
  controller: AbortController,
): RequestInit => {
  const defaultOptions: RequestInit = {
    credentials: 'same-origin',
    signal: controller.signal,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  };
  return mergeLeft(options, defaultOptions);
};

/**
 * The TypeScript definition of fetch has overloads for some reasons.
 * Seems like that causes Typescript to infer `input` and `init` as
 * `any` when define methods like so:
 * `const newFetch: typeof fetch: (input, init) => { ... }`
 *
 * So, we'll copy it here. If this is changed in the future, maybe this could
 * be removed.
 */
export type Fetch = (
  input: RequestInfo,
  init?: RequestInit,
) => Promise<Response>;

const useFetch = (): [Status, Fetch] => {
  const [status, setStatus] = useState<Status>(Status.NOT_STARTED);

  const abortControllerRef = useRef<AbortController | null>(null);

  const abortCurrentRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [abortControllerRef]);

  const newFetch: Fetch = useCallback(
    async (input, options?) => {
      abortCurrentRequest();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setStatus(Status.IN_PROGRESS);

      try {
        const response = await fetch(input, getOptions(options, controller));
        raiseOnServerError(response);
        setStatus(Status.SUCCESS);
        return response;
      } catch (reason) {
        setStatus(Status.ERROR);
        if (isNetworkError(reason)) {
          throw new NetworkError();
        } else if (isAborted(reason)) {
          throw new AbortError();
        } else {
          throw reason;
        }
      } finally {
        abortControllerRef.current = null;
      }
    },
    [abortCurrentRequest, abortControllerRef],
  );

  useEffect(() => {
    return () => {
      abortCurrentRequest();
    };
  }, [abortCurrentRequest]);

  return [status, newFetch];
};

export default useFetch;
